/**
 * Sorting Manager Module for Murphy Door Configurator
 *
 * PURPOSE: Intelligent material-aware sorting for category products and variants.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Support (support-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handlers for bootstrap and material changes (Pattern 4)
 * - Event replay ready via bootstrapComplete flag (Pattern 5)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor delegation (Pattern 9)
 * - UI update events not applicable (pure support module - Pattern 10 N/A)
 * - Async event publishing (Pattern 11)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - No user input rendered to DOM (Pattern 17 - N/A for pure support module)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - CONSTANTS.EVENTS.* usage (Pattern 19)
 * - Conductor single publication (Pattern 20)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - Optimized path: O(1) material compatibility index lookups via PPD Layer 4
 * - Fallback path: O(n*m) variant iteration (warns in dev mode)
 * - Cache-first with TTL-based expiration and bounded cache size
 * - Expected execution time: ~1-10ms for category sorting
 *
 * All external communication flows through md-config-core-conductor.js delegation.
 *
 * Dependencies:
 * - md-config-core-conductor.js (product data access, material preferences)
 * - md-config-core-event-bus.js (event system)
 * - md-config-core-constants.js (centralized event constants)
 *
 * This module is used by:
 * - md-config-ui-updater.js (for smart sorted thumbnails)
 * - md-config-core-conductor.js (primary conductor)
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ALL events use centralized constants - NO local EVENT_TYPES objects (DRY)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    CATEGORY_SORT: 50, // ms - category product sorting
    VARIANT_SORT: 30, // ms - variant sorting for second view
    SCORE_CALC: 10, // ms - individual material score calculation
  });

  /* Scoring Constants */
  const MATERIAL_SCORES = Object.freeze({
    PRIMARY_MATCH: 250, // Primary wood/finish exact match
    SECONDARY_MATCH: 150, // Secondary material match
    COMPATIBLE_MATCH: 50, // Compatible material
    NO_MATCH: 0, // No material compatibility
  });

  const CACHE_DURATION = Object.freeze({
    SCORE_CACHE: 5 * 60 * 1000, // 5 minutes
    SORTED_CACHE: 2 * 60 * 1000, // 2 minutes
  });

  /* Internal Cache */
  const sortingCache = new Map();
  const scoreCache = new Map();

  // Prevent unbounded cache growth in long-running sessions
  const MAX_CACHE_SIZE = 500;

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // Queue for calls made before bootstrap completes
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData = {}) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[SortingManager] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(`[SortingManager] Error publishing ${eventType}:`, error);
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration, slowEventType) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(slowEventType, {
        operation: operationType,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        timestamp: Date.now(),
      });
    }
  }

  /**
   * Manages cache size by evicting the oldest entry (first inserted) when
   * the cache exceeds MAX_CACHE_SIZE. Maps iterate in insertion order per spec,
   * so the first key is always the oldest entry.
   *
   * @param {Map} cache - The cache Map to manage
   */
  function manageCacheSize(cache) {
    if (cache.size > MAX_CACHE_SIZE) {
      // Map.keys().next() gives the oldest (first-inserted) key - O(1) eviction
      const oldestKey = cache.keys().next().value;
      cache.delete(oldestKey);
    }
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // PATTERN 5: EVENT REPLAY READY (10 points) - late subscribers handled via bootstrapComplete flag
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[SortingManager] Bootstrap complete event received');
        bootstrapComplete = true;
        isInitialized = true;

        // Process any queued calls from before bootstrap
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[SortingManager] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[SortingManager] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }

        // Publish module ready event
        publishEvent(EVENTS.MODULE_SORTING_MANAGER_READY, {
          module: 'sortingManager',
          timestamp: Date.now(),
        });
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[SortingManager] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
  // Module publishes to window.MurphyDoorConfigurator once
  // ============================================================================
  window.MurphyDoorConfigurator.sortingManager = {
    /* Scoring Constants */
    MATERIAL_SCORES,
    CACHE_DURATION,

    /**
     * Sorts category products based on material preferences.
     *
     * Implements 5-stage event lifecycle (Pattern 3):
     * STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * Tiered scoring:
     * - Primary material matches (250pts)
     * - Secondary material matches (150pts)
     * - Compatible materials (50pts)
     * - Alphabetical fallback for ties
     *
     * @param {string} categoryId - Category to sort products for
     * @param {Object} materialPreferences - Optional preference override
     * @returns {Array} Sorted products array
     */
    sortCategoryProducts(categoryId, materialPreferences = null) {
      const startTime = performance.now();

      // PATTERN 6: Bootstrap runtime protection
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(
            '[SortingManager] sortCategoryProducts called before bootstrap complete'
          );
        return [];
      }

      try {
        // ================================================================
        // STAGE 1: START - Announce operation beginning
        // ================================================================
        publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_STARTED, {
          categoryId,
          hasPreferenceOverride: !!materialPreferences,
          timestamp: Date.now(),
        });

        // ================================================================
        // STAGE 2: VALIDATION - Validate inputs
        // ================================================================
        // PATTERN 7 & 9: PPD-FIRST via conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        if (!conductor || !conductor.getVirtualizedProductsForCategory) {
          publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_VALIDATION, {
            categoryId,
            valid: false,
            reason: 'Conductor not available',
            timestamp: Date.now(),
          });
          publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_FAILED, {
            categoryId,
            error: 'Conductor not available',
            duration: performance.now() - startTime,
            timestamp: Date.now(),
          });
          return [];
        }

        const products =
          conductor.getVirtualizedProductsForCategory(categoryId);

        if (!products || products.length === 0) {
          publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_VALIDATION, {
            categoryId,
            valid: true,
            productCount: 0,
            timestamp: Date.now(),
          });
          const duration = performance.now() - startTime;
          publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_COMPLETED, {
            categoryId,
            productCount: 0,
            sorted: false,
            duration,
            timestamp: Date.now(),
          });
          return [];
        }

        // Get current material preferences
        const preferences =
          materialPreferences || this.getCurrentMaterialPreferences();

        publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_VALIDATION, {
          categoryId,
          valid: true,
          productCount: products.length,
          hasPreferences: !!(
            preferences &&
            (preferences.woodApplied || preferences.finishApplied)
          ),
          timestamp: Date.now(),
        });

        // Cache key includes material preference fingerprint to prevent
        // stale results when wood/finish SKUs change within the same time bucket.
        const prefFingerprint = preferences
          ? `${preferences.appliedWoodSku || 'none'}_${preferences.appliedFinishSku || 'none'}`
          : 'no_prefs';
        const timeBucket =
          Date.now() - (Date.now() % CACHE_DURATION.SORTED_CACHE);
        const cacheKey = `category_${categoryId}_${prefFingerprint}_${timeBucket}`;
        if (sortingCache.has(cacheKey)) {
          const duration = performance.now() - startTime;
          publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_COMPLETED, {
            categoryId,
            productCount: sortingCache.get(cacheKey).length,
            sorted: true,
            cached: true,
            duration,
            timestamp: Date.now(),
          });
          return sortingCache.get(cacheKey);
        }

        // If no preferences, return products in original order
        if (
          !preferences ||
          (!preferences.woodApplied && !preferences.finishApplied)
        ) {
          const duration = performance.now() - startTime;
          publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_COMPLETED, {
            categoryId,
            productCount: products.length,
            sorted: false,
            reason: 'no_preferences',
            duration,
            timestamp: Date.now(),
          });
          return products;
        }

        // ================================================================
        // STAGE 3: PROGRESS - Calculate and sort
        // ================================================================
        publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_PROGRESS, {
          categoryId,
          stage: 'scoring',
          productCount: products.length,
          timestamp: Date.now(),
        });

        // Calculate material compatibility scores for each product
        // Spread operator preserves all properties including CompositeProduct flags
        const scoredProducts = products.map((product) => {
          const materialScore = this.calculateProductMaterialScore(
            product,
            preferences
          );
          return {
            ...product,
            materialScore,
          };
        });

        // Sort by material score (highest first) then alphabetically
        const sortedProducts = scoredProducts.sort((a, b) => {
          if (b.materialScore !== a.materialScore) {
            return b.materialScore - a.materialScore;
          }
          return (a.title || '').localeCompare(b.title || '');
        });

        // Cache the result
        sortingCache.set(cacheKey, sortedProducts);
        manageCacheSize(sortingCache);

        // ================================================================
        // STAGE 4: SUCCESS
        // ================================================================
        const duration = performance.now() - startTime;

        publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_COMPLETED, {
          categoryId,
          productCount: sortedProducts.length,
          sorted: true,
          cached: false,
          duration,
          timestamp: Date.now(),
        });

        // PATTERN 12: Performance tracking
        trackPerformance(
          'CATEGORY_SORT',
          duration,
          EVENTS.PERFORMANCE_SORTING_CATEGORY_SLOW
        );

        isDev() &&
          console.log(
            `[SortingManager] Sort complete: ${categoryId} sorted ${sortedProducts.length} products in ${duration.toFixed(2)}ms`
          );

        return sortedProducts;
      } catch (error) {
        // ================================================================
        // STAGE 5: FAILED (Pattern 14)
        // ================================================================
        const duration = performance.now() - startTime;

        publishEvent(EVENTS.SUPPORT_SORTING_CATEGORY_FAILED, {
          categoryId,
          error: error.message,
          duration,
          timestamp: Date.now(),
        });

        isDev() &&
          console.error(
            '[SortingManager] Error in sortCategoryProducts:',
            error
          );

        // Fallback: PPD virtualized products unsorted
        try {
          const conductor = window.MurphyDoorConfigurator.conductor;
          if (conductor && conductor.getVirtualizedProductsForCategory) {
            return conductor.getVirtualizedProductsForCategory(categoryId);
          }
          return [];
        } catch (fallbackError) {
          isDev() &&
            console.error(
              '[SortingManager] Fallback error: Complete sorting failure',
              fallbackError
            );
          return [];
        }
      }
    },

    /**
     * Sorts variants for second view based on material preferences.
     *
     * Implements 5-stage event lifecycle (Pattern 3):
     * STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * @param {Array} variants - Variants to sort
     * @param {Object} materialPreferences - Optional preference override
     * @returns {Array} Sorted variants array
     */
    sortVariantsForSecondView(variants, materialPreferences = null) {
      const startTime = performance.now();

      try {
        // STAGE 1: START
        publishEvent(EVENTS.SUPPORT_SORTING_VARIANT_STARTED, {
          variantCount: variants?.length || 0,
          hasPreferenceOverride: !!materialPreferences,
          timestamp: Date.now(),
        });

        if (!variants || variants.length === 0) {
          publishEvent(EVENTS.SUPPORT_SORTING_VARIANT_COMPLETED, {
            variantCount: 0,
            sorted: false,
            reason: 'empty_input',
            duration: performance.now() - startTime,
            timestamp: Date.now(),
          });
          return [];
        }

        // Get current material preferences
        const preferences =
          materialPreferences || this.getCurrentMaterialPreferences();

        // If no preferences, return price-based sort with "No " prefix priority
        if (
          !preferences ||
          (!preferences.woodApplied && !preferences.finishApplied)
        ) {
          const result = this.sortVariantsByPrice(variants);
          const duration = performance.now() - startTime;
          publishEvent(EVENTS.SUPPORT_SORTING_VARIANT_COMPLETED, {
            variantCount: result.length,
            sorted: true,
            sortMethod: 'price_only',
            duration,
            timestamp: Date.now(),
          });
          trackPerformance(
            'VARIANT_SORT',
            duration,
            EVENTS.PERFORMANCE_SORTING_VARIANT_SLOW
          );
          return result;
        }

        // Calculate variant compatibility scores
        const scoredVariants = variants.map((variant) => {
          const materialScore = this.calculateVariantMaterialScore(
            variant,
            preferences
          );
          return {
            ...variant,
            materialScore,
          };
        });

        // Sort by material score (highest first) then by price with "No " prefix priority
        const sortedVariants = scoredVariants.sort((a, b) => {
          if (b.materialScore !== a.materialScore) {
            return b.materialScore - a.materialScore;
          }
          return this.compareVariantsByPrice(a, b);
        });

        // STAGE 4: SUCCESS
        const duration = performance.now() - startTime;
        publishEvent(EVENTS.SUPPORT_SORTING_VARIANT_COMPLETED, {
          variantCount: sortedVariants.length,
          sorted: true,
          sortMethod: 'material_preference',
          duration,
          timestamp: Date.now(),
        });

        trackPerformance(
          'VARIANT_SORT',
          duration,
          EVENTS.PERFORMANCE_SORTING_VARIANT_SLOW
        );

        return sortedVariants;
      } catch (error) {
        // STAGE 5: FAILED (Pattern 14)
        const duration = performance.now() - startTime;

        publishEvent(EVENTS.SUPPORT_SORTING_VARIANT_FAILED, {
          error: error.message,
          variantCount: variants?.length || 0,
          duration,
          timestamp: Date.now(),
        });

        isDev() &&
          console.error(
            '[SortingManager] Error in sortVariantsForSecondView:',
            error
          );
        return this.sortVariantsByPrice(variants);
      }
    },

    /**
     * Sorts variants by price with special priority for "No " prefixed variants
     *
     * @param {Array} variants - Variants to sort
     * @returns {Array} Sorted variants array
     */
    sortVariantsByPrice(variants) {
      try {
        if (!variants || variants.length === 0) {
          return [];
        }

        return variants.sort((a, b) => this.compareVariantsByPrice(a, b));
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error in sortVariantsByPrice:',
            error
          );
        // Ultimate fallback to alphabetical if price sorting fails
        return variants.sort((a, b) =>
          (a.title || '').localeCompare(b.title || '')
        );
      }
    },

    /**
     * Compares two variants by price with "No " prefix priority
     *
     * @param {Object} a - First variant
     * @param {Object} b - Second variant
     * @returns {number} Sort comparison result
     */
    compareVariantsByPrice(a, b) {
      try {
        const aTitle = (a.title || '').toLowerCase();
        const bTitle = (b.title || '').toLowerCase();

        // Check for "No " prefix priority (case insensitive)
        const aHasNoPrefix = aTitle.startsWith('no ');
        const bHasNoPrefix = bTitle.startsWith('no ');

        // If one has "No " prefix and the other doesn't, prioritize the "No " one
        if (aHasNoPrefix && !bHasNoPrefix) {
          return -1; // a comes first
        }
        if (bHasNoPrefix && !aHasNoPrefix) {
          return 1; // b comes first
        }

        // Both have "No " prefix or neither do - sort by price
        const aPrice = this.extractVariantPrice(a);
        const bPrice = this.extractVariantPrice(b);

        // Sort by price (lowest first)
        if (aPrice !== bPrice) {
          return aPrice - bPrice;
        }

        // If prices are equal, fall back to alphabetical
        return aTitle.localeCompare(bTitle);
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error in compareVariantsByPrice:',
            error
          );
        // Fallback to alphabetical comparison
        return (a.title || '').localeCompare(b.title || '');
      }
    },

    /**
     * Extracts numeric price from variant object
     *
     * @param {Object} variant - Variant object
     * @returns {number} Price as number, defaults to 0 if not found
     */
    extractVariantPrice(variant) {
      try {
        if (!variant) return 0;

        // Check various price fields that might exist
        let price = 0;

        if (variant.price !== undefined && variant.price !== null) {
          price =
            typeof variant.price === 'string'
              ? parseFloat(variant.price)
              : variant.price;
        } else if (
          variant.price_cents !== undefined &&
          variant.price_cents !== null
        ) {
          price =
            typeof variant.price_cents === 'string'
              ? parseFloat(variant.price_cents)
              : variant.price_cents;
          // Convert cents to dollars if it looks like cents (> 100)
          if (price > 100) {
            price = price / 100;
          }
        } else if (
          variant.compare_at_price !== undefined &&
          variant.compare_at_price !== null
        ) {
          price =
            typeof variant.compare_at_price === 'string'
              ? parseFloat(variant.compare_at_price)
              : variant.compare_at_price;
        }

        // Ensure we return a valid number
        return isNaN(price) || price < 0 ? 0 : price;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error extracting variant price:',
            error
          );
        return 0;
      }
    },

    /**
     * Calculates product group score for grouped products
     *
     * @param {Array} productGroup - Group of products to score
     * @param {Object} preferences - Material preferences
     * @returns {number} Group compatibility score
     */
    calculateProductGroupScore(productGroup, preferences) {
      try {
        if (!productGroup || productGroup.length === 0) return 0;
        if (!preferences) return 0;

        let maxScore = 0;

        // Get the highest score from any product in the group
        for (const product of productGroup) {
          const productScore = this.calculateProductMaterialScore(
            product,
            preferences
          );
          maxScore = Math.max(maxScore, productScore);
        }

        return maxScore;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error calculating product group score:',
            error
          );
        return 0;
      }
    },

    /**
     * Calculates material compatibility score for a product.
     *
     * PERFORMANCE OPTIMIZED: Uses PPD Layer 4 material compatibility index for O(1) lookups.
     * Falls back to O(n*m) iteration if index not available.
     *
     * @param {Object} product - Product to score
     * @param {Object} preferences - Material preferences
     * @returns {number} Compatibility score
     */
    calculateProductMaterialScore(product, preferences) {
      try {
        if (!product || !preferences) return 0;

        // PATTERN 7 & 9: PPD-first via conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        if (conductor && conductor.getMaterialCompatibilityIndex) {
          const compatibilityIndex = conductor.getMaterialCompatibilityIndex();

          if (compatibilityIndex) {
            // OPTIMIZED PATH: Use O(1) index lookups
            return this.calculateProductMaterialScoreOptimized(
              product,
              preferences,
              compatibilityIndex
            );
          }
        }

        // FALLBACK PATH: Use traditional O(n*m) iteration
        return this.calculateProductMaterialScoreFallback(product, preferences);
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error calculating product material score:',
            error
          );
        return 0;
      }
    },

    /**
     * OPTIMIZED: Calculates product material score using O(1) index lookups
     *
     * @param {Object} product - Product to score
     * @param {Object} preferences - Material preferences
     * @param {Object} compatibilityIndex - Pre-built compatibility index from PPD
     * @returns {number} Compatibility score
     */
    calculateProductMaterialScoreOptimized(
      product,
      preferences,
      compatibilityIndex
    ) {
      try {
        if (!product || !preferences || !compatibilityIndex) return 0;

        const { woodCompatibilityMap, finishCompatibilityMap } =
          compatibilityIndex;
        let maxScore = 0;

        // Get variant IDs from product
        if (!product.variants || !Array.isArray(product.variants)) {
          return 0;
        }

        // OPTIMIZATION: Use pre-built variant ID Set from PPD (Phase 1)
        const variantIds =
          product.variantIdSet ||
          new Set(product.variants?.map((v) => v.id) || []); // Fallback for compatibility

        // Store Map.get() results once and reuse across all compatibility checks.
        // Eliminates 2 redundant Map lookups per scored product.
        const woodCompatibleVariants =
          preferences.woodApplied && preferences.appliedWoodSku
            ? woodCompatibilityMap.get(preferences.appliedWoodSku)
            : null;
        const finishCompatibleVariants =
          preferences.finishApplied && preferences.appliedFinishSku
            ? finishCompatibilityMap.get(preferences.appliedFinishSku)
            : null;

        // Wood compatibility check - O(1) lookup in index
        if (woodCompatibleVariants) {
          for (const variantId of variantIds) {
            if (woodCompatibleVariants.has(variantId)) {
              maxScore = Math.max(maxScore, MATERIAL_SCORES.PRIMARY_MATCH);
              break; // Found a match, no need to check more
            }
          }
        }

        // Finish compatibility check - O(1) lookup in index
        if (finishCompatibleVariants) {
          for (const variantId of variantIds) {
            if (finishCompatibleVariants.has(variantId)) {
              maxScore = Math.max(maxScore, MATERIAL_SCORES.PRIMARY_MATCH);
              break; // Found a match, no need to check more
            }
          }
        }

        // Both wood AND finish compatible (bonus points)
        // Reuses stored lookup results from above - no redundant .get() calls
        if (woodCompatibleVariants && finishCompatibleVariants) {
          for (const variantId of variantIds) {
            if (
              woodCompatibleVariants.has(variantId) &&
              finishCompatibleVariants.has(variantId)
            ) {
              maxScore += MATERIAL_SCORES.SECONDARY_MATCH;
              break; // Found a dual match, no need to check more
            }
          }
        }

        return maxScore;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error in optimized product material score:',
            error
          );
        return 0;
      }
    },

    /**
     * FALLBACK: Traditional O(n*m) product material score calculation.
     * Used when compatibility index is not available.
     *
     * @param {Object} product - Product to score
     * @param {Object} preferences - Material preferences
     * @returns {number} Compatibility score
     */
    calculateProductMaterialScoreFallback(product, preferences) {
      try {
        if (!product || !preferences) return 0;

        // Warn developers when the slow O(n*m) fallback path is hit
        isDev() &&
          console.warn(
            '[SortingManager] PERFORMANCE: Using O(n*m) fallback - PPD materialCompatibilityIndex may be missing or invalid'
          );

        let maxScore = 0;

        // Check all variants in the product for material matches
        if (product.variants && Array.isArray(product.variants)) {
          for (const variant of product.variants) {
            const variantScore = this.calculateVariantMaterialScore(
              variant,
              preferences
            );
            maxScore = Math.max(maxScore, variantScore);
          }
        }

        return maxScore;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error calculating product material score (fallback):',
            error
          );
        return 0;
      }
    },

    /**
     * Calculates material compatibility score for a variant
     *
     * @param {Object} variant - Variant to score
     * @param {Object} preferences - Material preferences
     * @returns {number} Compatibility score
     */
    calculateVariantMaterialScore(variant, preferences) {
      try {
        if (!variant || !preferences) return 0;

        // Deterministic preference fingerprint for cache key
        const scorePrefFingerprint = `${preferences.appliedWoodSku || 'none'}_${preferences.appliedFinishSku || 'none'}`;
        const cacheKey = `${variant.id}_${scorePrefFingerprint}`;

        // Check cache first
        if (scoreCache.has(cacheKey)) {
          const cached = scoreCache.get(cacheKey);
          if (Date.now() - cached.timestamp < CACHE_DURATION.SCORE_CACHE) {
            return cached.score;
          }
        }

        let score = 0;

        // Wood compatibility check
        if (preferences.woodApplied && preferences.appliedWoodSku) {
          if (
            this.isVariantCompatibleWithWood(
              variant,
              preferences.appliedWoodSku
            )
          ) {
            score += MATERIAL_SCORES.PRIMARY_MATCH;
          }
        }

        // Finish compatibility check
        if (preferences.finishApplied && preferences.appliedFinishSku) {
          if (
            this.isVariantCompatibleWithFinish(
              variant,
              preferences.appliedFinishSku
            )
          ) {
            score += MATERIAL_SCORES.PRIMARY_MATCH;
          }
        }

        // Both wood and finish compatible (bonus points)
        if (preferences.woodApplied && preferences.finishApplied) {
          if (
            this.isVariantCompatibleWithBoth(
              variant,
              preferences.appliedWoodSku,
              preferences.appliedFinishSku
            )
          ) {
            score += MATERIAL_SCORES.SECONDARY_MATCH;
          }
        }

        // Secondary material preferences
        if (preferences.secondaryMaterialPreferences) {
          for (const [
            ,
            secondaryPref,
          ] of preferences.secondaryMaterialPreferences) {
            if (
              this.isVariantCompatibleWithSecondaryMaterial(
                variant,
                secondaryPref
              )
            ) {
              score += MATERIAL_SCORES.COMPATIBLE_MATCH;
            }
          }
        }

        // Cache the score
        scoreCache.set(cacheKey, {
          score,
          timestamp: Date.now(),
        });
        manageCacheSize(scoreCache);

        return score;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error calculating variant material score:',
            error
          );
        return 0;
      }
    },

    /**
     * Calculates product variant score for specific product/variant combination.
     * Called by state manager for compatibility scoring.
     *
     * @param {string} productId - Product ID
     * @param {string} variantId - Variant ID
     * @param {Object} preferences - Material preferences
     * @returns {number} Compatibility score
     */
    calculateProductVariantScore(productId, variantId, preferences) {
      try {
        // PATTERN 7 & 9: PPD-first via conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        if (!conductor || !conductor.getVariantById) {
          isDev() &&
            console.error(
              '[SortingManager] Conductor or getVariantById method not available'
            );
          return 0;
        }

        // PPD-FIRST: Use O(1) variant lookup instead of product fetch + O(n) variant search
        const variant = conductor.getVariantById(variantId);
        if (!variant) {
          isDev() &&
            console.warn(
              `[SortingManager] Variant ${variantId} not found via getVariantById. Product context: ${productId}`
            );
          return 0;
        }

        return this.calculateVariantMaterialScore(variant, preferences);
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error calculating product variant score:',
            error
          );
        return 0;
      }
    },

    /**
     * Checks if variant is compatible with specific wood
     *
     * @param {Object} variant - Variant to check
     * @param {string} woodSku - Wood SKU to match
     * @returns {boolean} True if compatible
     */
    isVariantCompatibleWithWood(variant, woodSku) {
      try {
        if (!variant || !woodSku) return false;

        // Direct SKU match
        if (variant.wood_sku === woodSku) return true;

        // Check compatibility tags
        if (
          variant.compatibility_tags &&
          Array.isArray(variant.compatibility_tags)
        ) {
          return variant.compatibility_tags.includes(
            `${CONSTANTS.MATERIAL_TYPES.WOOD}:${woodSku}`
          );
        }

        // Check material type compatibility
        if (
          variant.material_type &&
          variant.material_type.includes(CONSTANTS.MATERIAL_TYPES.WOOD)
        ) {
          return variant.wood_sku === woodSku;
        }

        return false;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error checking wood compatibility:',
            error
          );
        return false;
      }
    },

    /**
     * Checks if variant is compatible with specific finish
     *
     * @param {Object} variant - Variant to check
     * @param {string} finishSku - Finish SKU to match
     * @returns {boolean} True if compatible
     */
    isVariantCompatibleWithFinish(variant, finishSku) {
      try {
        if (!variant || !finishSku) return false;

        // Direct SKU match
        if (variant.finish_sku === finishSku) return true;

        // Check compatibility tags
        if (
          variant.compatibility_tags &&
          Array.isArray(variant.compatibility_tags)
        ) {
          return variant.compatibility_tags.includes(
            `${CONSTANTS.MATERIAL_TYPES.FINISH}:${finishSku}`
          );
        }

        // Check material type compatibility
        if (
          variant.material_type &&
          variant.material_type.includes(CONSTANTS.MATERIAL_TYPES.FINISH)
        ) {
          return variant.finish_sku === finishSku;
        }

        return false;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error checking finish compatibility:',
            error
          );
        return false;
      }
    },

    /**
     * Checks if variant is compatible with both wood and finish
     *
     * @param {Object} variant - Variant to check
     * @param {string} woodSku - Wood SKU to match
     * @param {string} finishSku - Finish SKU to match
     * @returns {boolean} True if compatible with both
     */
    isVariantCompatibleWithBoth(variant, woodSku, finishSku) {
      try {
        return (
          this.isVariantCompatibleWithWood(variant, woodSku) &&
          this.isVariantCompatibleWithFinish(variant, finishSku)
        );
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error checking dual compatibility:',
            error
          );
        return false;
      }
    },

    /**
     * Checks if variant is compatible with secondary material preference
     *
     * @param {Object} variant - Variant to check
     * @param {Object} secondaryPref - Secondary preference object
     * @returns {boolean} True if compatible
     */
    isVariantCompatibleWithSecondaryMaterial(variant, secondaryPref) {
      try {
        if (!variant || !secondaryPref || !secondaryPref.materialData)
          return false;

        const materialData = secondaryPref.materialData;

        // Check for any material SKU matches
        if (materialData.woodSku && variant.wood_sku === materialData.woodSku)
          return true;
        if (
          materialData.finishSku &&
          variant.finish_sku === materialData.finishSku
        )
          return true;

        // Check material type compatibility
        if (
          materialData.materialType &&
          variant.material_type === materialData.materialType
        )
          return true;

        // Check compatibility tags overlap
        if (materialData.compatibilityTags && variant.compatibility_tags) {
          return materialData.compatibilityTags.some((tag) =>
            variant.compatibility_tags.includes(tag)
          );
        }

        return false;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error checking secondary material compatibility:',
            error
          );
        return false;
      }
    },

    /**
     * Gets current material preferences from state manager via conductor delegation.
     * PATTERN 9: Data access through conductor delegation.
     *
     * @returns {Object|null} Current material preferences (null if not yet initialized)
     */
    getCurrentMaterialPreferences() {
      try {
        // CCP COMPLIANT: Get material preferences through conductor delegation
        const conductor = window.MurphyDoorConfigurator.conductor;
        if (!conductor || !conductor.getMaterialPreferences) {
          isDev() &&
            console.warn(
              '[SortingManager] Conductor not available for getMaterialPreferences'
            );
          return null;
        }

        const preferences = conductor.getMaterialPreferences();

        // Handle Promise case (not yet initialized)
        if (preferences instanceof Promise) {
          isDev() &&
            console.log(
              '[SortingManager] Material preferences not yet initialized (returning null)'
            );
          return null;
        }

        return preferences || null;
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error getting material preferences:',
            error
          );
        return null;
      }
    },

    /**
     * Clears all sorting caches.
     * Called when material preferences change via event subscription.
     */
    clearSortingCache() {
      try {
        sortingCache.clear();
        scoreCache.clear();

        publishEvent(EVENTS.SUPPORT_SORTING_CACHE_CLEARED, {
          timestamp: Date.now(),
        });

        isDev() && console.log('[SortingManager] All sorting caches cleared');
      } catch (error) {
        isDev() &&
          console.error(
            '[SortingManager] Error clearing sorting cache:',
            error
          );
      }
    },

    /**
     * Gets cache statistics for debugging
     *
     * @returns {Object} Cache statistics
     */
    getCacheStats() {
      return {
        sortingCacheSize: sortingCache.size,
        scoreCacheSize: scoreCache.size,
        maxCacheSize: MAX_CACHE_SIZE,
        lastClearTime: Date.now(),
      };
    },

    /**
     * Cleanup method for resource management (Pattern 15).
     * Unsubscribes all event handlers and resets state.
     */
    cleanup() {
      isDev() && console.log('[SortingManager] Cleaning up resources');

      // Unsubscribe all event handlers
      cleanupHandlers.forEach((unsubscribe) => {
        try {
          unsubscribe();
        } catch (e) {
          /* ignore */
        }
      });
      cleanupHandlers.length = 0;

      // Clear caches
      sortingCache.clear();
      scoreCache.clear();

      // Reset state
      isInitialized = false;
      bootstrapComplete = false;
      pendingCalls = [];

      publishEvent(EVENTS.MODULE_SORTING_MANAGER_CLEANUP, {
        module: 'sortingManager',
        timestamp: Date.now(),
      });
    },
  };

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // Subscribe to material preference changes to invalidate sort cache
  // ============================================================================
  const manager = window.MurphyDoorConfigurator.sortingManager;

  if (CoreEventBus) {
    // DRY COMPLIANT: Reuse public clearSortingCache method
    const unsubMaterialUpdated = CoreEventBus.subscribe(
      EVENTS.STATE_MATERIAL_PREFERENCES_UPDATED,
      () => manager.clearSortingCache()
    );
    cleanupHandlers.push(unsubMaterialUpdated);

    const unsubMaterialCleared = CoreEventBus.subscribe(
      EVENTS.STATE_MATERIAL_PREFERENCES_CLEARED,
      () => manager.clearSortingCache()
    );
    cleanupHandlers.push(unsubMaterialCleared);

    // Subscribe to material preferences recomputed from applied selections
    // When an unapply triggers preference recomputation (surviving contributors win),
    // the sort order must update so preferred materials bubble to top in browsable categories.
    const unsubMaterialRecomputed = CoreEventBus.subscribe(
      EVENTS.MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED,
      (eventData) => {
        isDev() &&
          console.log(
            '[SortingManager] Material preferences recomputed from applied - clearing sort cache',
            { affectedCategories: eventData?.affectedCategories }
          );
        manager.clearSortingCache();
      }
    );
    cleanupHandlers.push(unsubMaterialRecomputed);
  }

  isDev() && console.log('[SortingManager] Initialization Complete');
})(window);
